import { errorMessage } from "@streamoid/errors";
import React, { useEffect } from "react";
import { getServices } from "../../utils/services";
import "./style.css";
import Modal from "../Modal";
import EditService from "./EditService";
import { notify } from "../../utils/utils";
import {
  getDashboardVersion,
  updateDashboardVersion,
} from "../../utils/dashboardDetails";
import { getAllUsers } from "../../utils/account";

export default function Services() {
  const [allServices, setAllServices] = React.useState([]);
  const [showSelectedService, setShowSelectedService] = React.useState(null);
  const [version, setVersion] = React.useState("");
  const [previousVersion, setPreviousVersion] = React.useState("");
  const [versionHistory, setVersionHistory] = React.useState([]);
  const [versionError, setVersionError] = React.useState("");
  const [isVersionLoading, setIsVersionLoading] = React.useState(false);
  const [isSavingVersion, setIsSavingVersion] = React.useState(false);
  const [teamsData, setTeamsData] = React.useState({});

  useEffect(() => {
    fetchServices();
    fetchDashboardVersion();
    getAllUsers().then((res) => {
      if (res.users) {
        setTeamsData(
          res.users.reduce((acc: any, user: any) => {
            acc[user._id] = user;
            return acc;
          }, {}),
        );
      }
    });
  }, []);

  const fetchServices = () => {
    getServices()
      .then((res) => {
        console.log(res);
        setAllServices(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const fetchDashboardVersion = () => {
    setIsVersionLoading(true);
    getDashboardVersion()
      .then((res) => {
        console.log("@getDashboardVersion", res);
        const dashboardData = res?.data || res || {};
        const fetchedVersion = dashboardData?.currentVersion;
        setVersion(fetchedVersion);
        setPreviousVersion(dashboardData?.previousVersion || "");
        setVersionHistory(dashboardData?.history || []);
      })
      .catch((err) => {
        console.log(err);
        notify("Error fetching dashboard version", "error");
      })
      .finally(() => {
        setIsVersionLoading(false);
      });
  };

  const handleUpdateDashboardVersion = () => {
    const sanitizedVersion = version.trim();

    if (!sanitizedVersion) {
      setVersionError("Please enter a version value.");
      return;
    }
    setVersionError("");
    setIsSavingVersion(true);

    updateDashboardVersion({ version: sanitizedVersion })
      .then((res) => {
        console.log(res);
        setVersion(sanitizedVersion);
        notify("Dashboard version updated successfully");
        fetchDashboardVersion();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsSavingVersion(false);
      });
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) return value;
    return dateValue.toLocaleString();
  };

  return (
    <>
      <div className="dashboard-settings-section">
        <div className="section-heading">Dashboard Settings</div>
        <div className="settings-container">
          <div className="version-summary">
            <div className="summary-item">
              <div className="summary-label">Current version</div>
              <div className="summary-value">{version || "-"}</div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Previous version</div>
              <div className="summary-value">{previousVersion || "-"}</div>
            </div>
          </div>

          <div className="version-header">
            <div className="version-title">Version</div>
            <div className="version-subtitle">
              Keep the dashboard release number up to date.
            </div>
          </div>

          <div className="version-controls">
            <input
              className="version-input"
              value={version}
              placeholder="e.g. 1.0.1"
              onChange={(e) => setVersion(e.target.value)}
              disabled={isVersionLoading || isSavingVersion}
            />
            <button
              className="version-save-btn"
              onClick={handleUpdateDashboardVersion}
              disabled={isVersionLoading || isSavingVersion}
            >
              {isSavingVersion ? "Saving..." : "Update"}
            </button>
          </div>
          <div className="version-footnote">
            {versionError ? (
              <span className="version-error-text">{versionError}</span>
            ) : (
              <span className="version-status">
                {isVersionLoading
                  ? "Loading current version..."
                  : version
                    ? `Current version: ${version}${
                        previousVersion ? ` (previous: ${previousVersion})` : ""
                      }`
                    : "No version set yet."}
              </span>
            )}
          </div>

          <div className="version-history">
            <div className="history-heading">Version history</div>
            {!versionHistory.length ? (
              <div className="history-empty">No history available.</div>
            ) : (
              <ul className="history-list">
                {versionHistory.map((entry: any) => (
                  <li
                    key={entry?._id || `${entry?.version}-${entry?.createdAt}`}
                    className="history-row"
                  >
                    <div className="history-version">
                      v{entry?.version || "-"}
                    </div>
                    <div className="history-meta">
                      <span>{formatDateTime(entry?.createdAt)}</span>
                      {entry?.createdBy && (
                        <span className="history-separator">•</span>
                      )}
                      {entry?.createdBy && (
                        <span className="history-author">
                          by{" "}
                          {entry.createdBy && teamsData[entry.createdBy]
                            ? teamsData[entry.createdBy]?.email ||
                              entry.createdBy
                            : "-"}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-settings-section services-section">
        <div className="section-heading">Services</div>
        <div className="service-container">
          {allServices.map((service: any) => {
            return (
              <div
                key={service._id}
                className="service-card"
                onClick={() => {
                  setShowSelectedService(service);
                }}
              >
                <div>
                  <img
                    src={service.imgUrl}
                    alt={service.imgUrl}
                    className="w-[40px] h-[40px]"
                  />
                </div>
                <h2>Name: {service.name}</h2>
                <p>Description: {service.desc}</p>
                <p>Page: {service.pageUrl}</p>
                <p>Display Name: {service.displayName}</p>
                <p>Icon URL: {service.imgUrl}</p>
              </div>
            );
          })}
        </div>
      </div>

      {showSelectedService && (
        <Modal onClose={() => setShowSelectedService(null)}>
          <EditService
            onClose={() => setShowSelectedService(null)}
            serviceDetails={showSelectedService}
            fetchServices={fetchServices}
          ></EditService>
        </Modal>
      )}
    </>
  );
}
